import { useSearch } from '@tanstack/react-router';
import { Route as CommunicationsRoute } from 'src/routes/communications';
import type { CommunicationsSearch } from 'src/modules/shared/schemas/communications-search.schema';
import {
  type Contact,
  CustomFieldEntityType,
  CustomFieldType,
} from 'src/api/generated';
import { useUpdateContactMutation } from 'src/modules/communications/hooks/api/contacts-mutations';
import { CustomFieldEditor } from '../../custom-fields/custom-field-editor';
import { useCustomFieldDefinitions } from 'src/modules/shared/hooks/api/custom-fields/definitions';
import { useFunnelCustomFieldDefinitions } from 'src/modules/shared/hooks/api/crm/funnels/funnel-custom-field-definitions';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { resolveDateCustomFieldIncludeTime } from 'src/modules/shared/utils/custom-field-definition.util';
import { getCustomFieldKey } from 'src/modules/shared/utils/custom-fields.util';
import { createCustomFieldFromDefinition } from 'src/modules/shared/utils/custom-fields-factory.util';
import { useCustomFieldListOptions } from 'src/modules/shared/hooks/api/custom-fields/list-options';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { useMemo } from 'react';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { DetailsSectionReveal } from './details-section-reveal';

interface ContactCustomFieldsSectionProps {
  contact: Contact;
  loading?: boolean;
  error?: Error | null;
}

export const ContactCustomFieldsSection: React.FC<
  ContactCustomFieldsSectionProps
> = ({ contact, loading = false, error = null }) => {
  const { t } = useI18n();
  const { updateContact, loading: isUpdatingContact } =
    useUpdateContactMutation();

  const { funnelId } = useSearch({
    from: CommunicationsRoute.id,
    select: (search: CommunicationsSearch) => ({
      funnelId: search.funnelId,
    }),
  });

  const hasFunnel = !!funnelId;

  const {
    definitions: fallbackDefinitions,
    loading: loadingDefinitions,
    error: definitionsError,
  } = useCustomFieldDefinitions({
    entityType: CustomFieldEntityType.CONTACT,
    enabled: !hasFunnel,
  });

  const {
    definitions: funnelOrderedDefinitions,
    loading: loadingFunnelDefinitions,
    error: funnelDefinitionsError,
  } = useFunnelCustomFieldDefinitions({
    funnelId: hasFunnel ? funnelId : undefined,
    enabled: hasFunnel,
  });

  const definitions = hasFunnel
    ? funnelOrderedDefinitions.filter(
        (d) => d.entityType === CustomFieldEntityType.CONTACT
      )
    : fallbackDefinitions;

  const listDefinitionIds = definitions
    .filter((def) => def.fieldType === CustomFieldType.LIST)
    .map((def) => def.id);

  const optionsQuery = useMemo(() => {
    if (listDefinitionIds.length === 0) return null;

    return createQuery()
      .equals('definitionId', listDefinitionIds)
      .take(100)
      .build();
  }, [listDefinitionIds]);

  const { options: listOptions } = useCustomFieldListOptions({
    ...(optionsQuery ?? {}),
    enabled: listDefinitionIds.length > 0,
  });

  const optionsMap = useMemo(() => {
    const map: Record<number, { label: string; value: string }[]> = {};

    listOptions.forEach((option) => {
      const definitionOptions = (map[option.definitionId] ??= []);

      definitionOptions.push({
        label: option.label ?? option.value,
        value: option.value,
      });
    });

    return map;
  }, [listOptions]);

  const handleUpdateContactField = async (
    fieldId: string | number,
    value: unknown
  ) => {
    const customFields = {
      ...(contact.customFields ?? {}),
      [fieldId.toString()]: value,
    };

    await updateContact({
      id: contact.id,
      data: {
        customFields,
      },
    });
  };

  const isLoadingDefinitions = hasFunnel
    ? loadingFunnelDefinitions
    : loadingDefinitions;
  const hasDefinitionsError = hasFunnel
    ? funnelDefinitionsError
    : definitionsError;

  if (error || hasDefinitionsError) {
    return (
      <ErrorState
        title={t(
          'communications.contactPanel.contact.customFields.error.title'
        )}
        description={t(
          'communications.contactPanel.contact.customFields.error.description'
        )}
      />
    );
  }

  if (loading || isLoadingDefinitions || definitions.length === 0) {
    return null;
  }

  const items = definitions.map((definition) => {
    const prefixedKey = getCustomFieldKey(
      definition.fieldType,
      definition.fieldKey
    );
    const value =
      (contact.customFields as Record<string, unknown> | undefined)?.[
        prefixedKey
      ] ?? null;

    const customField = createCustomFieldFromDefinition(
      definition.id,
      definition.fieldKey,
      definition.fieldType,
      definition.fieldLabel,
      value,
      definition.fieldType === CustomFieldType.LIST
        ? optionsMap[definition.id]
        : undefined,
      resolveDateCustomFieldIncludeTime(
        definition.fieldType,
        definition.includeTime
      )
    );

    return (
      <CustomFieldEditor
        key={definition.fieldKey}
        field={customField}
        onSave={(value) => handleUpdateContactField(prefixedKey, value)}
        disabled={isUpdatingContact}
        loading={isUpdatingContact}
        entityType={CustomFieldEntityType.CONTACT}
        entityId={contact.id}
        size="md"
        variant="neutral"
        appearance="soft"
        layout="stacked"
      />
    );
  });

  return <DetailsSectionReveal items={items} initiallyVisible={2} />;
};
